import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useProfile } from '../../../contexts/ProfileContext';
import {
  deleteCreatorCustomGame,
  listCreatorCustomGames,
  updateCustomGameZoom,
} from '../../../utils/customGameApi';
import {
  CUSTOM_DEFAULT_FULL_ZOOM_OUT_ROW,
  CUSTOM_DEFAULT_ZOOM_RADIUS,
  CUSTOM_MIN_FULL_ZOOM_OUT_ROW,
  CUSTOM_UNLIMITED_CLUE_REVEAL_CAP,
  CUSTOM_ZOOM_BOUNDS,
  getCustomZoomLevelForRadius,
} from '../../../constants/games/zoomedInCustomGame';
import ZoomedImagePreview from '../shared/ZoomedImagePreview';
import ImagePreviewModal from '../shared/ImagePreviewModal';
import CustomZoomOriginPicker from './CustomZoomOriginPicker';
import CustomZoomRadiusHelpText from './CustomZoomRadiusHelpText';
import './CustomManageGamesCard.css';

const clamp01 = (value) => Math.min(1, Math.max(0, value));

const toNumberOr = (value, fallback) => {
  const n = Number(value);
  return Number.isFinite(n) ? n : fallback;
};

const resolveFullZoomOutUpperBound = (maxGuesses) => {
  if (maxGuesses === null || maxGuesses === undefined || maxGuesses === '') {
    return CUSTOM_UNLIMITED_CLUE_REVEAL_CAP;
  }
  const parsedMaxGuesses = Number(maxGuesses);
  if (!Number.isFinite(parsedMaxGuesses) || parsedMaxGuesses <= 0) {
    return CUSTOM_UNLIMITED_CLUE_REVEAL_CAP;
  }
  return Math.max(CUSTOM_MIN_FULL_ZOOM_OUT_ROW, Math.floor(parsedMaxGuesses));
};

const clampFullZoomOutRow = (value, maxGuesses) => {
  const upperBound = resolveFullZoomOutUpperBound(maxGuesses);
  const parsed = Number(value);
  const fallback = Math.min(CUSTOM_DEFAULT_FULL_ZOOM_OUT_ROW, upperBound);
  if (!Number.isFinite(parsed)) return fallback;
  return Math.max(CUSTOM_MIN_FULL_ZOOM_OUT_ROW, Math.min(upperBound, Math.floor(parsed)));
};

const resolveClueRevealUpperBound = (maxGuesses) => {
  if (maxGuesses === null || maxGuesses === undefined || maxGuesses === '') {
    return CUSTOM_UNLIMITED_CLUE_REVEAL_CAP;
  }
  const parsedMaxGuesses = Number(maxGuesses);
  if (!Number.isFinite(parsedMaxGuesses) || parsedMaxGuesses <= 0) {
    return CUSTOM_UNLIMITED_CLUE_REVEAL_CAP;
  }
  return Math.max(1, Math.floor(parsedMaxGuesses));
};

const clampClueRevealRow = (value, maxGuesses) => {
  const upperBound = resolveClueRevealUpperBound(maxGuesses);
  const parsed = Number(value);
  if (!Number.isFinite(parsed)) return 1;
  return Math.max(1, Math.min(upperBound, Math.floor(parsed)));
};

const toZoomDraft = (game) => ({
  x: clamp01(toNumberOr(game?.zoomOriginX, 0.5)),
  y: clamp01(toNumberOr(game?.zoomOriginY, 0.5)),
  radius: toNumberOr(game?.zoomRadius, CUSTOM_DEFAULT_ZOOM_RADIUS),
  fullZoomOutRow: clampFullZoomOutRow(game?.fullZoomOutRow, game?.maxGuesses),
  clueRevealRow: clampClueRevealRow(game?.clueRevealRow, game?.maxGuesses),
  clueText: String(game?.clueText || ''),
});

const CustomManageGamesCard = ({ refreshToken = 0 }) => {
  const { user } = useProfile();
  const [loading, setLoading] = useState(false);
  const [games, setGames] = useState([]);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [previewGame, setPreviewGame] = useState(null);
  const [editingGameId, setEditingGameId] = useState('');
  const [zoomDraftById, setZoomDraftById] = useState({});
  const [savingZoomId, setSavingZoomId] = useState('');
  const [confirmDeleteId, setConfirmDeleteId] = useState('');
  const [deletingId, setDeletingId] = useState('');

  const loadGames = useCallback(async () => {
    if (!user) {
      setGames([]);
      return;
    }
    setLoading(true);
    setError('');
    try {
      const payload = await listCreatorCustomGames({ limit: 100, offset: 0 });
      setGames(payload.games || []);
      setConfirmDeleteId('');
      setEditingGameId('');
      setZoomDraftById({});
    } catch (apiError) {
      setError(apiError?.message || 'Failed to load your custom games.');
    } finally {
      setLoading(false);
    }
  }, [user]);

  useEffect(() => {
    loadGames();
  }, [loadGames, refreshToken]);

  const beginEdit = (game) => {
    setEditingGameId(game.gameId);
    setZoomDraftById((previous) => ({
      ...previous,
      [game.gameId]: toZoomDraft(game),
    }));
    setSuccess('');
    setError('');
  };

  const updateDraft = (gameId, partial) => {
    setZoomDraftById((previous) => ({
      ...previous,
      [gameId]: {
        ...toZoomDraft(games.find((entry) => entry.gameId === gameId)),
        ...(previous[gameId] || {}),
        ...(partial || {}),
      },
    }));
  };

  const saveZoom = async (game) => {
    const draft = zoomDraftById[game.gameId] || toZoomDraft(game);
    setSavingZoomId(game.gameId);
    setError('');
    setSuccess('');

    try {
      const payload = await updateCustomGameZoom({
        gameId: game.gameId,
        zoomOriginX: draft.x,
        zoomOriginY: draft.y,
        zoomRadius: draft.radius,
        fullZoomOutRow: draft.fullZoomOutRow,
        clueRevealRow: draft.clueRevealRow,
        clueText: String(draft.clueText || '').trim(),
      });

      setGames((previous) =>
        previous.map((entry) =>
          entry.gameId === game.gameId
            ? {
                ...entry,
                zoomOriginX: payload.zoomOriginX,
                zoomOriginY: payload.zoomOriginY,
                zoomRadius: payload.zoomRadius,
                fullZoomOutRow: payload.fullZoomOutRow,
                clueRevealRow: payload.clueRevealRow,
                clueText: payload.clueText,
              }
            : entry,
        ),
      );

      setEditingGameId('');
      setSuccess('Custom game settings saved.');
    } catch (apiError) {
      setError(apiError?.message || 'Failed to save zoom settings.');
    } finally {
      setSavingZoomId('');
    }
  };

  const deleteGame = async (game) => {
    if (confirmDeleteId !== game.gameId) {
      setConfirmDeleteId(game.gameId);
      setSuccess('');
      setError('');
      return;
    }

    setDeletingId(game.gameId);
    setError('');
    setSuccess('');
    try {
      await deleteCreatorCustomGame({ gameId: game.gameId });
      setGames((previous) => previous.filter((entry) => entry.gameId !== game.gameId));
      setConfirmDeleteId('');
      if (editingGameId === game.gameId) setEditingGameId('');
      setSuccess('Custom game and image deleted.');
    } catch (apiError) {
      setError(apiError?.message || 'Failed to delete custom game.');
    } finally {
      setDeletingId('');
    }
  };

  const copyShareLink = async (slug) => {
    const base = window?.location?.origin || '';
    const shareUrl = `${base}/custom/${slug}`;
    try {
      await navigator.clipboard.writeText(shareUrl);
      setSuccess('Share link copied.');
      setError('');
    } catch {
      setError('Could not copy share link.');
    }
  };

  const emptyState = useMemo(() => !loading && games.length === 0, [games.length, loading]);

  return (
    <section className="custom-manage-card">
      <div className="custom-manage-card__header">
        <h3>Manage Your Created Games</h3>
        <button type="button" onClick={loadGames} disabled={loading}>
          {loading ? 'Refreshing...' : 'Refresh'}
        </button>
      </div>

      {!user ? (
        <p className="custom-manage-card__hint">Sign in to manage your custom games.</p>
      ) : null}

      {error ? <div className="custom-manage-card__error">{error}</div> : null}
      {success ? <div className="custom-manage-card__success">{success}</div> : null}

      {emptyState ? (
        <div className="custom-manage-card__empty">No custom games yet. Create one above.</div>
      ) : null}

      {games.map((game) => {
        const isEditing = editingGameId === game.gameId;
        const draft = zoomDraftById[game.gameId] || toZoomDraft(game);
        const fullZoomOutUpperBound = resolveFullZoomOutUpperBound(game.maxGuesses);
        const fullZoomOutRowOptions = Array.from(
          { length: fullZoomOutUpperBound - CUSTOM_MIN_FULL_ZOOM_OUT_ROW + 1 },
          (_, index) => index + CUSTOM_MIN_FULL_ZOOM_OUT_ROW,
        );
        const clueRevealUpperBound = resolveClueRevealUpperBound(game.maxGuesses);
        const clueRevealRowOptions = Array.from(
          { length: clueRevealUpperBound },
          (_, index) => index + 1,
        );
        const isDeleteArmed = confirmDeleteId === game.gameId;
        const isDeleting = deletingId === game.gameId;
        const isSavingZoom = savingZoomId === game.gameId;

        return (
          <article key={game.gameId} className="custom-manage-card__item">
            <div className="custom-manage-card__item-main">
              <ZoomedImagePreview
                imageUrl={game.imageUrl}
                zoomLevel={getCustomZoomLevelForRadius(draft.radius)}
                zoomOrigin={{ x: draft.x, y: draft.y }}
                size={112}
                borderRadius={10}
              />

              <div className="custom-manage-card__meta">
                <div className="custom-manage-card__slug">{game.slug}</div>
                <div className="custom-manage-card__meta-line">
                  {game.style} • {game.wordLength} letters
                </div>
                <div className="custom-manage-card__meta-line">
                  {game.maxGuesses ? `max ${game.maxGuesses} guesses` : 'unlimited guesses'}
                </div>
                <div className="custom-manage-card__meta-line">
                  clue row {game.clueRevealRow}
                </div>
                <div className="custom-manage-card__meta-line">
                  clue text {String(game.clueText || '').trim() ? 'set' : 'empty'}
                </div>
                <div className="custom-manage-card__meta-line">
                  full zoom-out row {clampFullZoomOutRow(game.fullZoomOutRow, game.maxGuesses)}
                </div>
              </div>
            </div>

            <div className="custom-manage-card__actions">
              <button type="button" onClick={() => setPreviewGame(game)}>
                Preview Start Zoom
              </button>
              <button type="button" onClick={() => copyShareLink(game.slug)}>
                Copy Share Link
              </button>
              {isEditing ? (
                <button type="button" onClick={() => setEditingGameId('')}>
                  Close Settings
                </button>
              ) : (
                <button type="button" onClick={() => beginEdit(game)}>
                  Edit Settings
                </button>
              )}
              <button
                type="button"
                onClick={() => deleteGame(game)}
                disabled={isDeleting}
                className={isDeleteArmed ? 'custom-manage-card__danger' : ''}
              >
                {isDeleting
                  ? 'Deleting...'
                  : isDeleteArmed
                  ? 'Confirm Permanent Delete'
                  : 'Delete Game + Image'}
              </button>
            </div>

            {isEditing ? (
              <div className="custom-manage-card__editor">
                <CustomZoomOriginPicker
                  imageUrl={game.imageUrl}
                  value={{ x: draft.x, y: draft.y }}
                  zoomLevel={getCustomZoomLevelForRadius(draft.radius)}
                  onChange={(next) =>
                    updateDraft(game.gameId, {
                      x: clamp01(toNumberOr(next?.x, draft.x)),
                      y: clamp01(toNumberOr(next?.y, draft.y)),
                    })
                  }
                />

                <label className="custom-manage-card__field">
                  <span>Zoom Radius</span>
                  <input
                    type="number"
                    min="0.01"
                    max="1"
                    step="0.01"
                    value={draft.radius}
                    onChange={(event) =>
                      updateDraft(game.gameId, {
                        radius: toNumberOr(event.target.value, draft.radius),
                      })
                    }
                  />
                </label>
                <CustomZoomRadiusHelpText className="custom-manage-card__hint" />

                <label className="custom-manage-card__field">
                  <span>Full Zoom-Out Row</span>
                  <select
                    value={draft.fullZoomOutRow}
                    onChange={(event) =>
                      updateDraft(game.gameId, {
                        fullZoomOutRow: clampFullZoomOutRow(event.target.value, game.maxGuesses),
                      })
                    }
                  >
                    {fullZoomOutRowOptions.map((value) => (
                      <option key={value} value={value}>
                        {value}
                      </option>
                    ))}
                  </select>
                </label>

                <label className="custom-manage-card__field">
                  <span>Clue Reveal Row</span>
                  <select
                    value={draft.clueRevealRow}
                    onChange={(event) =>
                      updateDraft(game.gameId, {
                        clueRevealRow: clampClueRevealRow(event.target.value, game.maxGuesses),
                      })
                    }
                  >
                    {clueRevealRowOptions.map((value) => (
                      <option key={value} value={value}>
                        {value}
                      </option>
                    ))}
                  </select>
                </label>

                <label className="custom-manage-card__field custom-manage-card__field--wide">
                  <span>Clue Text (Optional)</span>
                  <textarea
                    value={draft.clueText}
                    rows={3}
                    placeholder="Write a clue that appears once clue reveal row is reached."
                    onChange={(event) =>
                      updateDraft(game.gameId, {
                        clueText: event.target.value,
                      })
                    }
                  />
                </label>

                <button
                  type="button"
                  onClick={() => saveZoom(game)}
                  disabled={isSavingZoom}
                  className="custom-manage-card__save-btn"
                >
                  {isSavingZoom ? 'Saving...' : 'Save Settings'}
                </button>
              </div>
            ) : null}
          </article>
        );
      })}

      {previewGame ? (
        <ImagePreviewModal
          imageUrl={previewGame.imageUrl}
          alt="Custom start zoom preview"
          zoomLevel={getCustomZoomLevelForRadius(previewGame.zoomRadius)}
          zoomOrigin={{
            x: toNumberOr(previewGame.zoomOriginX, 0.5),
            y: toNumberOr(previewGame.zoomOriginY, 0.5),
          }}
          zoomBounds={CUSTOM_ZOOM_BOUNDS}
          onClose={() => setPreviewGame(null)}
        />
      ) : null}
    </section>
  );
};

export default CustomManageGamesCard;
